import { Injectable } from '@angular/core';
import { FormArray, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { DynamicPropertiesConfig, DynamicPropertyDependency, DynamicPropertyValue } from './dynamic-properties.interface';
import { OfferService } from '@features/profile/offer/offer.service';
import { DYNAMIC_PROPERTYES, PROPERTIES_FORM_NAME } from './properties-form.config';
import { ProfileService } from '@features/profile/profile.service';
import { ClonerService } from '@services/cloner.service';
import { AccountService } from '@services/account.service';
import moment from 'moment';
import { OfferApiService } from '@services/remote-api';
import { MessageService } from '@ui-components/messages/messages.service';
import { v4 as uuidv4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class PropertiesFormService {
  rentalPropertyValues = {};
  ofvCarData = {
    carBrandId: '',
    carModelId: '',
    carRegModelYear: '',
    carModelYear: '',
    carVariantId: ''
  };
  defaultPropertiesConfig: any;
  defaultPropertiesConfigLoaded = false;
  defaultPropertiesConfigLoader = false;
  dynamicPropertiesConfig: DynamicPropertiesConfig | null = null;
  dynamicPropertiesConfigLoaded = false;
  dynamicPropertiesConfigLoader = false;
  readonly dynamicPropertiesLoaded$ = new Subject<void>();
  private readonly userModifiedPropertyNames = new WeakMap<object, Set<string>>();

  constructor(
    private offerService: OfferService,
    private profileService: ProfileService,
    private clonerService: ClonerService,
    private accountService: AccountService,
    private offerApiService: OfferApiService,
    private messageService: MessageService,
    private translateService: TranslateService,
  ) {}

  clearOfvData(): void {
    this.ofvCarData = {
      carBrandId: '',
      carModelId: '',
      carRegModelYear: '',
      carModelYear: '',
      carVariantId: '',
    };
  }

  handleSelectConfig(listData = [], keyName: string, valueName: string): any {
    let configData = [];

    if (listData && listData.length) {
      configData = listData.map(listItem => {
        return {
          key: listItem[keyName],
          value: listItem[valueName]
        };
      });
    }

    return configData;
  }

  isManualCarCreationFlow(properties): boolean {
    return properties.noVehicleRegNo;
  }

  populateInitOfvData(properties): void {
    this.ofvCarData.carBrandId = properties.ofvBrandId;
    this.ofvCarData.carModelId = properties.ofvModelId;
    this.ofvCarData.carRegModelYear = properties.vehicleRegistrationYear;
    this.ofvCarData.carModelYear = properties.ofvYearModel;
    this.ofvCarData.carVariantId = properties.ofvVariantId;
  }

  clearManualFlowDataProperties(productData): void {
    productData.properties.ofvBrandId = '';
    productData.properties.ofvModelId = '';
    productData.properties.vehicleRegistrationYear = '';
    productData.properties.ofvYearModel = '';
    productData.properties.ofvVariantId = '';
    productData.properties.vehicleLicenseNumber = null;
  }

  disablePropertyAndClearValidators(property: any): void {
    property.get('inputData').value.propertyConfig.disabled = true;
    property.get('value').clearValidators();
    property.get('value').updateValueAndValidity();
  }

  disableProperty(property: any): void {
    property.get('inputData').value.propertyConfig.disabled = true;
  }

  enableProperty(property: any): void {
    property.get('inputData').value.propertyConfig.disabled = false;
  }

  setPropertyValue(property, productData, valueToSet): void {
    property.get('value').setValue(valueToSet);
    productData.properties[property.get('inputData').value.propertyName] = valueToSet;
  }

  enableVehicleLicenceNumberProperty(property: any): void {
    property.get('inputData').value.propertyConfig.disabled = false;
    property.get('value').setValidators([
      Validators.required,
      Validators.pattern(/[a-zA-Z]{2}\d{5}/)
    ]);
    property.get('value').updateValueAndValidity();
  }

  formatProperties(formData: any, productToChange: any): any {
    Object.keys(formData.value).forEach(key => {
      const propertySection = formData.value[key];

      propertySection.forEach(propertyData => {
        if (!productToChange.properties) {
          productToChange.properties = {};
        }

        if (propertyData.inputData['index'] !== undefined) {
          this.setAdditionalArrayProperties(productToChange, propertyData);
        } else {
          this.setProperties(productToChange, propertyData);
        }
      });
    });

    return productToChange;
  }

  removeAdditionalArrayProperties(propertyName, propertyIndex, productToChange): void {
    if (productToChange && productToChange.properties && productToChange.properties[propertyName]) {
      productToChange.properties[propertyName].splice(propertyIndex, 1);
    }
  }

  fetchIndexFromFormName(formArrayName): number {
    return +formArrayName.substring(formArrayName.indexOf('_') + 1, formArrayName.length);
  }

  fetchPropertyArrayName(formArrayName): string {
    return formArrayName.substring(0, formArrayName.indexOf('_'));
  }

  isArrayForm(formArrayName): boolean {
    return formArrayName && formArrayName.indexOf('_') > -1;
  }

  isPropertyListAdded(propertiesForm, propertyConfigList, formArrayName = PROPERTIES_FORM_NAME): any {
    const form = propertiesForm.get(formArrayName) as FormArray;

    let isPropertiesAdded = true;

    propertyConfigList.forEach(config => {
      if (!form.controls.find(control => control.value.inputData.propertyName === config.name)) {
        isPropertiesAdded = false;
      }
    });

    return isPropertiesAdded;
  }

  isAllowToAddRentalProperties(): any {
    const allowedValues = {
      productPackage: 'SUPER',
      wtcHouseownerPrimary: true,
      houseIsRented: true
    };

    return this.compareObjects(this.rentalPropertyValues, allowedValues);
  }

  populateRentalPropertyFormValues(fieldSettings): any {
    this.rentalPropertyValues[fieldSettings.name] = fieldSettings.value;
  }

  getFormValueByPropertyName(form: any, propertyName: string): any {
    const items = form.get(PROPERTIES_FORM_NAME) as FormArray;

    return items.controls.find(control => control.value.inputData.propertyName === propertyName).value.value;
  }


  populateRentalPropertyValues(properties): any {
    if (properties) {
      this.rentalPropertyValues['productPackage'] = properties.productPackage;
      this.rentalPropertyValues['wtcHouseownerPrimary'] = properties.wtcHouseownerPrimary;
      this.rentalPropertyValues['houseIsRented'] = properties.houseIsRented;
    }
  }

  isEditView(productData): boolean {
    return productData.productType;
  }

  getDefaultProperties(offerId: string): void {
    this.defaultPropertiesConfigLoaded = false;
    this.defaultPropertiesConfig = null;
    this.defaultPropertiesConfigLoader = true;

    this.offerApiService.getDefaultProperties(offerId).subscribe({
      next: config => {
      this.defaultPropertiesConfigLoaded = true;
      this.defaultPropertiesConfigLoader = false;
      this.defaultPropertiesConfig = config;
    },
      error: err => {
      this.defaultPropertiesConfigLoaded = false;
      this.defaultPropertiesConfigLoader = false;
      this.messageService.setGlobalError(err);
    },
    });
  }

  getDynamicProperties(): void {
    this.dynamicPropertiesConfigLoaded = false;
    this.dynamicPropertiesConfig = null;
    this.dynamicPropertiesConfigLoader = true;

    this.offerApiService.getDynamicProperties().subscribe({
      next: config => {
        this.dynamicPropertiesConfigLoaded = true;
        this.dynamicPropertiesConfigLoader = false;
        this.dynamicPropertiesConfig = config;
        this.dynamicPropertiesLoaded$.next();
      },
      error: err => {
        this.dynamicPropertiesConfigLoaded = false;
        this.dynamicPropertiesConfigLoader = false;
        this.messageService.setGlobalError(err);
      },
    });
  }

  setDefaultParametersData(propertiesConfig: any, productType: string, boatType = ''): any[] {
    const parametersFormDataToSet = [];
    const offerJson = this.offerService.currentOfferData.offerJson;
    const configData = this.clonerService.deepClone(this.defaultPropertiesConfig);

    let propertiesDataToSet = configData[productType] ? configData[productType] : {};

    Object.keys(propertiesDataToSet).forEach(propertyKey => {
      if (this.profileService.checkPropertiesForData(propertiesDataToSet[propertyKey]) === 'object') {
        delete propertiesDataToSet[propertyKey];
      }
    });

    if (productType === 'BOAT' && this.accountService.getUser().isBoatTypes) {
      if (boatType) {
        propertiesDataToSet = configData[boatType];
      } else {
        propertiesDataToSet = configData['MotorBoat'];
      }
    }

    const propertiesSetConfigDynamic = [
      {
        name: 'objectZipCode',
        value: offerJson && offerJson.acceptOfferData ? offerJson.acceptOfferData.postCode : ''
      },
      {
        name: 'zipCode',
        value: offerJson && offerJson.acceptOfferData ? offerJson.acceptOfferData.postCode : ''
      },
      {
        name: 'wtcBoatpostcode',
        value: offerJson && offerJson.acceptOfferData ? offerJson.acceptOfferData.postCode : ''
      },
      {
        name: 'calculatedClientAge',
        value: this.fetchCalculatedClientAge()
      },
      {
        name: 'vehicleDrivingLengthReadingDate',
        value: moment().format('DD-MM-YYYY')
      }
    ];

    propertiesSetConfigDynamic.forEach(config => {
      if (config.value && propertiesConfig.find(property => property.name === config.name)) {
        propertiesDataToSet[config.name] = config.value;
        parametersFormDataToSet.push(config);
      }
    });

    Object.keys(propertiesDataToSet).forEach(propertyStaticKey => {
      if (propertiesConfig.find(property => property.name === propertyStaticKey)) {
        parametersFormDataToSet.push(
          {
            name: propertyStaticKey,
            value: propertiesDataToSet[propertyStaticKey]
          }
        );
      }
    });

    // remove wtcBoatGroup from defaults properties
    if (boatType) {
     delete propertiesDataToSet.wtcBoatGroup;
    }
    // remove wtcBoatGroup from defaults properties

    return [parametersFormDataToSet, propertiesDataToSet];
  }

  markUserModifiedProperty(productData: object, propertyName: string): void {
    if (!productData || !propertyName) {
      return;
    }

    const modifiedProperties = this.userModifiedPropertyNames.get(productData) ?? new Set<string>();
    modifiedProperties.add(propertyName);
    this.userModifiedPropertyNames.set(productData, modifiedProperties);
  }

  applyDynamicDefaultProperties(
    productType: string,
    productData: any,
    propertiesConfig: Array<{ name: string }> = [],
    isEdit = false
  ): Record<string, DynamicPropertyValue> {
    const dependencies = this.dynamicPropertiesConfig?.[productType]?.dependencies || [];
    const changes: Record<string, DynamicPropertyValue> = {};

    if (!productData || !dependencies.length) {
      return changes;
    }

    productData.properties = productData.properties || {};

    const conditionChanged = dependencies.some(dependency =>
      Object.keys(dependency.conditions || {}).some(name => this.isUserModifiedProperty(productData, name))
    );

    this.getDependentPropertyNames(dependencies).forEach(propertyName => {
      const isInForm = !propertiesConfig.length || propertiesConfig.some(property => property.name === propertyName);

      if (!isInForm || this.isUserModifiedProperty(productData, propertyName)) {
        return;
      }

      const currentValue = productData.properties[propertyName];
      const isEmpty = currentValue === undefined || currentValue === null || currentValue === '';
      const isStillDefault = this.isStillDefault(productData, propertyName);
      const dynamicValue = this.findDynamicValue(dependencies, productData.properties, propertyName);

      if (dynamicValue !== undefined) {
        const canSet = isStillDefault || (isEmpty && (!isEdit || conditionChanged));

        if (canSet) {
          this.setDefault(productData, propertyName, dynamicValue, changes);
        }

        return;
      }

      if (!conditionChanged) {
        return;
      }

      const isDynamicLeftover = isStillDefault && this.isDynamicValue(dependencies, propertyName, currentValue);

      if (!isDynamicLeftover && !isEmpty) {
        return;
      }

      const companyDefault = this.getCompanyDefault(productType, propertyName);

      if (companyDefault !== undefined) {
        this.setDefault(productData, propertyName, companyDefault, changes);
      } else if (isDynamicLeftover) {
        this.clearDefault(productData, propertyName, changes);
      }
    });

    return changes;
  }

  private getDependentPropertyNames(dependencies: DynamicPropertyDependency[]): string[] {
    const names = dependencies
      .map(dependency => dependency.dependentProperty?.property)
      .filter(name => !!name);

    return Array.from(new Set(names));
  }

  private findDynamicValue(
    dependencies: DynamicPropertyDependency[],
    properties: Record<string, unknown>,
    propertyName: string
  ): DynamicPropertyValue | undefined {
    let dynamicValue: DynamicPropertyValue | undefined;

    dependencies.forEach(dependency => {
      const conditions = dependency.conditions || {};
      const conditionNames = Object.keys(conditions);
      const isForProperty = dependency.dependentProperty?.property === propertyName;
      const conditionsMatch = conditionNames.length > 0
        && conditionNames.every(name => this.valuesMatch(properties[name], conditions[name]));

      if (isForProperty && conditionsMatch) {
        dynamicValue = dependency.dependentProperty.propertyValue;
      }
    });

    return dynamicValue;
  }

  private isDynamicValue(dependencies: DynamicPropertyDependency[], propertyName: string, value: unknown): boolean {
    return dependencies.some(dependency =>
      dependency.dependentProperty?.property === propertyName
      && this.valuesMatch(dependency.dependentProperty.propertyValue, value)
    );
  }

  private getCompanyDefault(productType: string, propertyName: string): DynamicPropertyValue | undefined {
    const companyDefault = this.defaultPropertiesConfig?.[productType]?.[propertyName];

    if (companyDefault === undefined || companyDefault === null || typeof companyDefault === 'object') {
      return undefined;
    }

    return companyDefault;
  }

  private isStillDefault(productData: any, propertyName: string): boolean {
    const defaults = productData.defaultProperties;

    return !!defaults
      && Object.prototype.hasOwnProperty.call(defaults, propertyName)
      && this.valuesMatch(defaults[propertyName], productData.properties?.[propertyName]);
  }

  private isUserModifiedProperty(productData: object, propertyName: string): boolean {
    return !!productData && !!propertyName && !!this.userModifiedPropertyNames.get(productData)?.has(propertyName);
  }

  private setDefault(
    productData: any,
    propertyName: string,
    value: DynamicPropertyValue,
    changes: Record<string, DynamicPropertyValue>
  ): void {
    productData.defaultProperties = productData.defaultProperties || {};
    productData.defaultProperties[propertyName] = value;
    productData.properties[propertyName] = value;
    changes[propertyName] = value;
  }

  private clearDefault(productData: any, propertyName: string, changes: Record<string, DynamicPropertyValue>): void {
    delete productData.properties[propertyName];
    delete productData.defaultProperties[propertyName];
    changes[propertyName] = '';
  }

  private valuesMatch(left: unknown, right: unknown): boolean {
    if (left === right) {
      return true;
    }

    if (left == null || right == null || left === '' || right === '') {
      return false;
    }

    return String(left) === String(right);
  }

  fetchHouseAdditionalBuildingDescription(houseBuildingTypeValue, houseSquareMetersValue): string {
    if (houseBuildingTypeValue && houseSquareMetersValue) {
      return `${this.translateService.instant('PROPERTIES_FORM.SELECT_LIST.HOUSE_BUILDING_TYPE.' + houseBuildingTypeValue)}, ${houseSquareMetersValue} kvm`;
    } else if (houseBuildingTypeValue && !houseSquareMetersValue) {
      return `${this.translateService.instant('PROPERTIES_FORM.SELECT_LIST.HOUSE_BUILDING_TYPE.' + houseBuildingTypeValue)}`;
    }

    return '';
  }

  private fetchCalculatedClientAge(): string {
    const ssnValue = this.offerService.fetchSsn();

    if (ssnValue) {
      return this.profileService.getAgeFromDOB(
        this.profileService.getDOBfromSsn(ssnValue)
      );
    }

    return '';
  }

  private setAdditionalArrayProperties(productToChange, propertyData): void {
    const additionalArrayIndex = propertyData.inputData.index;

    if (!productToChange.properties[propertyData.inputData.propertyArrayName]) {
      productToChange.properties[propertyData.inputData.propertyArrayName] = [];

      if (!productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex]) {
        productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex] = {};
      }
    }

    if (!productToChange.properties[propertyData.inputData.propertyArrayName]) {
      productToChange.properties[propertyData.inputData.propertyArrayName] = [];
    }

    if (!productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex]) {
      productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex] = {};
    }

    if (propertyData.inputData.propertyName === 'productSumInsured' && !isNaN(propertyData.value)) {
      productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex]
        [propertyData.inputData.propertyName] = Number(propertyData.value);
    } else {
      productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex]
        [propertyData.inputData.propertyName] = propertyData.value;
    }

    if (!productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex].nestedPropertyId) {
      productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex].nestedPropertyId =
        uuidv4().replace(/-/g, '');
    }



    if (propertyData.inputData.propertyArrayName === 'houseAdditionalBuildings') {
      productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex].nestedPropertyName =
        this.fetchHouseAdditionalBuildingDescription(
          productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex].houseBuildingType,
          productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex].houseSquareMeters
        );
    }

    if (propertyData.inputData.propertyArrayName === 'houseWithContents') {
      productToChange.properties[propertyData.inputData.propertyArrayName][additionalArrayIndex].nestedPropertyName =
        this.translateService.instant(`PROPERTIES_FORM.TITLES.houseWithContents`);
    }

  }

  private setProperties(productToChange, propertyData): void {
    switch (propertyData.inputData.propertyName) {
      case DYNAMIC_PROPERTYES.VEHICLE_MODEL.name: {
        break;
      }
      case DYNAMIC_PROPERTYES.VEHICLE_BRAND.name: {
        break;
      }
      case 'vehicleLicenseNumber': {
        if (propertyData.value !== null) {
          productToChange.properties[propertyData.inputData.propertyName] = propertyData.value;
        }
        break;
      }
      case 'vehicleBonus': {
        if (propertyData.value && propertyData.value.indexOf(',') > -1) {
          const valueData = propertyData.value.split(',');

          productToChange.properties[propertyData.inputData.propertyName] = valueData[0];
          productToChange.properties['vehicleBonusYears'] = valueData[1];
        } else {
          productToChange.properties[propertyData.inputData.propertyName] = propertyData.value;
          delete productToChange.properties['vehicleBonusYears'];
        }
        break;
      }
      case 'travelDuration': {
        productToChange.properties['travelDurationType'] = 'DAYS';
        productToChange.properties[propertyData.inputData.propertyName] = propertyData.value;
        break;
      }
      case 'houseIsRented': {
        if (propertyData.value === 'true') {
          productToChange.properties[propertyData.inputData.propertyName] = true;
        } else if (propertyData.value === 'false') {
          productToChange.properties[propertyData.inputData.propertyName] = false;
        } else {
          productToChange.properties[propertyData.inputData.propertyName] = propertyData.value;
        }
        break;
      }
      case 'wtcBuildingConstructionFactor': {
        propertyData.value === 'false' ?
        productToChange.properties[propertyData.inputData.propertyName] = false :
        productToChange.properties[propertyData.inputData.propertyName] = propertyData.value;
        break;
      }
      default: {
        if (
          propertyData.inputData.propertyConfig?.omitEmptyString &&
          (propertyData.value === '' || propertyData.value === null || propertyData.value === undefined)
        ) {
          delete productToChange.properties[propertyData.inputData.propertyName];
          break;
        }
        productToChange.properties[propertyData.inputData.propertyName] = propertyData.value;
        break;
      }
    }
  }

  private compareObjects(object1, object2): boolean {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (object1[key] !== object2[key]) {
        return false;
      }
    }

    return true;
  }
}
